import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Receipt, Search, Send, Trash2, CheckCircle2, Upload, Plus, X, Car, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["Parking Ticket", "Traffic Citation", "Red-Light Camera", "Speed Camera", "Toll", "Impound", "Other"];

const STATUS_UI = {
  unmatched: { label: "Unmatched", cls: "text-white/60 border-white/20 bg-white/5" },
  matched: { label: "Matched", cls: "text-[#F2B705] border-[#F2B705]/40 bg-[#F2B705]/10" },
  sent: { label: "Sent / Billed", cls: "text-blue-300 border-blue-400/40 bg-blue-400/10" },
  paid: { label: "Paid", cls: "text-green-400 border-green-500/40 bg-green-500/10" },
};

function StatBox({ label, value, money, tone }) {
  const tones = { gold: "text-[#F2B705]", ok: "text-green-400", blue: "text-blue-300" };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="text-white/45 text-xs uppercase tracking-widest">{label}</div>
      <div className={`font-display font-black text-3xl mt-1.5 ${tones[tone] || "text-white"}`}>{money ? `$${value}` : value}</div>
    </div>
  );
}

function LogForm({ token, onSaved, onClose }) {
  const empty = { violation_date: "", type: "Traffic Citation", amount: "", plate: "", location: "", notes: "", photo_path: null };
  const [form, setForm] = useState(empty);
  const [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [matching, setMatching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const findRenter = async () => {
    if (!form.violation_date) { toast.error("Enter the violation date first."); return; }
    setMatching(true);
    try {
      const { data } = await axios.post(`${API}/citations/match`, { violation_date: form.violation_date, plate: form.plate, vehicle: "" }, { headers: { Authorization: `Bearer ${token}` } });
      setMatches(data.matches);
      if (data.matches.length === 0) toast.info("No rental found for that date/plate. You can still save it as unmatched.");
    } catch {
      toast.error("Could not search for a match.");
    } finally {
      setMatching(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.path);
      toast.success("Ticket photo uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.violation_date || !form.amount) { toast.error("Violation date and amount are required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, amount: parseFloat(form.amount) || 0,
        booking_id: selected?.booking_id || null, agreement_id: selected?.agreement_id || null,
        renter_name: selected?.renter_name || "", renter_email: selected?.renter_email || "",
      };
      await axios.post(`${API}/citations`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Citation logged.");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save citation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#F2B705]/30 rounded-xl p-5 space-y-4" data-testid="citation-log-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">Log a Ticket</h3>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/45">Violation Date *</label>
          <input data-testid="citation-date" type="date" value={form.violation_date} onChange={(e) => set("violation_date", e.target.value)} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white/85" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/45">Type</label>
          <select data-testid="citation-type" value={form.type} onChange={(e) => set("type", e.target.value)} className="w-full bg-[#0C0C0C] border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white/85">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/45">Amount ($) *</label>
          <input data-testid="citation-amount" type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/45">License Plate</label>
          <input data-testid="citation-plate" value={form.plate} onChange={(e) => set("plate", e.target.value)} placeholder="e.g. ABC1234" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/45">Location</label>
          <input data-testid="citation-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Where it occurred" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2" />
        </div>
        <label data-testid="citation-photo" className={`flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed rounded-lg px-4 self-end h-[42px] transition-colors ${form.photo_path ? "border-green-500/50 bg-green-500/5 text-green-400" : "border-white/15 hover:border-[#F2B705]/50 text-white/60"}`}>
          {uploading ? <Loader2 className="animate-spin" size={16} /> : form.photo_path ? <CheckCircle2 size={16} /> : <Upload size={16} />}
          <span className="text-sm">{form.photo_path ? "Photo added" : "Upload ticket photo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      <textarea data-testid="citation-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Notes (optional)" className="w-full bg-[#0C0C0C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />

      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-white/70">Who had this vehicle on <b className="text-white">{form.violation_date || "the violation date"}</b>?</p>
          <button data-testid="citation-find-renter" onClick={findRenter} disabled={matching} className="flex items-center gap-2 text-sm border border-[#F2B705]/50 text-[#F2B705] px-4 py-2 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-50">
            {matching ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />} Find Renter
          </button>
        </div>
        {matches && matches.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="citation-matches">
            {matches.map((mObj) => (
              <button key={mObj.booking_id} data-testid={`citation-match-${mObj.booking_id}`} onClick={() => setSelected(mObj)}
                className={`w-full text-left flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${selected?.booking_id === mObj.booking_id ? "border-[#F2B705] bg-[#F2B705]/10" : "border-white/10 hover:border-white/25"}`}>
                <div>
                  <div className="font-medium">{mObj.renter_name} {selected?.booking_id === mObj.booking_id && <CheckCircle2 size={14} className="inline text-[#F2B705] ml-1" />}</div>
                  <div className="text-white/45 text-xs flex items-center gap-2"><Car size={11} /> {mObj.vehicle} {mObj.plate ? `· ${mObj.plate}` : ""} · {mObj.start_date} → {mObj.return_date}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <button data-testid="citation-save" onClick={save} disabled={saving} className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} {selected ? `Save Citation for ${selected.renter_name}` : "Save Citation (unmatched)"}
      </button>
    </div>
  );
}

export default function CitationsManager({ token, onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchData = () => axios.get(`${API}/citations`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setData(r.data))
    .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); })
    .finally(() => setLoading(false));

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [token]);

  const action = async (id, fn, label) => {
    try { await fn(); toast.success(label); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  const charge = (id) => action(id, () => axios.post(`${API}/citations/${id}/charge`, {}, { headers: { Authorization: `Bearer ${token}` } }), "Citation sent & added to renter's balance.");
  const markPaid = (id) => action(id, () => axios.post(`${API}/citations/${id}/paid`, {}, { headers: { Authorization: `Bearer ${token}` } }), "Marked paid.");
  const remove = (id) => { if (window.confirm("Delete this citation?")) action(id, () => axios.delete(`${API}/citations/${id}`, { headers: { Authorization: `Bearer ${token}` } }), "Deleted."); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load citations.</p>;
  const s = data.stats;

  return (
    <div data-testid="citations-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-2xl flex items-center gap-2"><Receipt className="text-[#F2B705]" size={22} /> Traffic Tickets / Citations</h2>
          <p className="text-white/45 text-sm mt-1">Log tickets mailed to you, auto-match the renter, and bill them (+ $35 admin fee).</p>
        </div>
        <button data-testid="citation-new-btn" onClick={() => setShowForm((v) => !v)} className="flex items-center gap-2 bg-[#F2B705] text-black font-bold px-4 py-2.5 rounded-md hover:bg-white transition-colors">
          <Plus size={18} /> Log a Ticket
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Matched" value={s.matched} tone="gold" />
        <StatBox label="Sent / Billed" value={s.sent} tone="blue" />
        <StatBox label="Paid" value={s.paid} tone="ok" />
        <StatBox label="Outstanding" value={s.outstanding} money tone="gold" />
      </div>

      {showForm && <LogForm token={token} onSaved={fetchData} onClose={() => setShowForm(false)} />}

      {data.citations.length === 0 ? (
        <div data-testid="citations-empty" className="text-center py-16 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">
          No citations logged yet. Click "Log a Ticket" when one arrives in the mail.
        </div>
      ) : (
        <div className="space-y-3">
          {data.citations.map((c) => {
            const ui = STATUS_UI[c.status] || STATUS_UI.unmatched;
            return (
              <div key={c.id} data-testid={`citation-row-${c.id}`} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-semibold">
                      {c.type} <span className="text-white/40">· {c.violation_date}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${ui.cls}`}>{ui.label}</span>
                    </div>
                    <div className="text-white/55 text-sm mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5">
                      <span>Amount: <b className="text-white/80">${c.amount?.toFixed(2)}</b></span>
                      <span>Admin fee: <b className="text-white/80">${c.admin_fee?.toFixed(2)}</b></span>
                      <span>Total: <b className="text-[#F2B705]">${c.total?.toFixed(2)}</b></span>
                      {c.plate && <span>Plate: {c.plate}</span>}
                      {c.location && <span>{c.location}</span>}
                    </div>
                    <div className="text-white/45 text-xs mt-1">
                      {c.renter_name ? <>Billed to: <b className="text-white/70">{c.renter_name}</b> {c.renter_email ? `· ${c.renter_email}` : ""}</> : "Not yet matched to a renter"}
                    </div>
                    {c.notes && <p className="text-white/40 text-xs mt-1 italic">{c.notes}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
                  {c.booking_id && c.status !== "paid" && (
                    <button data-testid={`citation-charge-${c.id}`} onClick={() => charge(c.id)} className="text-xs flex items-center gap-1.5 border border-[#F2B705]/50 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors">
                      <Send size={13} /> {c.status === "sent" ? "Resend" : "Send & Charge"}
                    </button>
                  )}
                  {c.status === "sent" && (
                    <button data-testid={`citation-paid-${c.id}`} onClick={() => markPaid(c.id)} className="text-xs flex items-center gap-1.5 border border-green-500/40 text-green-400 px-3 py-1.5 rounded hover:bg-green-500 hover:text-white transition-colors">
                      <CheckCircle2 size={13} /> Mark Paid
                    </button>
                  )}
                  <button data-testid={`citation-delete-${c.id}`} onClick={() => remove(c.id)} className="text-xs flex items-center gap-1.5 border border-white/15 text-white/50 px-3 py-1.5 rounded hover:text-red-400 transition-colors ml-auto">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
